import { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { InputAdornment, Stack, Typography, Box, IconButton, TextField, Link } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import axios from '../utils/axios';
import { objectToFormData } from '../utils/objectToFormData';
import { UserContext } from '../context/UserProvider';
import LoginFormWrapper from './LoginFormWrapper';

export default function LoginForm({ wrapperSX = null })
{
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);

  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const [returnUrl, setReturnUrl] = useState(searchParams.get('returnUrl'));
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [formValue, setFormValue] = useState({
    password: "",
    email: ""
  });

  useEffect(() => {
    setReturnUrl(searchParams.get('returnUrl') || '');
  }, [searchParams]);


  const onSubmit = () => {
    setShowError(true);
    if (formValue.password && formValue.email) {
      setLoading(true);
      axios.post("/account/login", objectToFormData({ ...formValue, returnUrl }))
        .then(res => {
          if (!res.data.success) enqueueSnackbar(res.data.errorMessage ?? 'Login failed', { variant: 'error' });
          else {
            setUser(res.data.user);
            navigate(res.data.redirectUri ?? `/home`);
          }
        }).catch(() => enqueueSnackbar('Login failed', { variant: 'error' }))
        .finally(() => setLoading(false))
    }
  };

  return <LoginFormWrapper wrapperSX={wrapperSX}>
    <Stack spacing={2.5} >
      <Box>
        <Typography sx={{ fontSize: 24, fontWeight: 700, }}> Account Login </Typography>
        <Typography sx={{ fontSize: 14, color: "grey", lineHeight: 1.6, mb: 3, mt: 1 }}>
          If you are already a member you can login with your email address and password.
        </Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, mb: 0.8 }}> Email </Typography>
        <TextField name="email" placeholder="Enter email" fullWidth value={formValue.email}
          onChange={(e) => setFormValue(form => ({ ...form, email: e.target.value }))} error={showError && !formValue.email}
          helperText={showError && !formValue.email ? "You must enter an email" : ""} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, mb: 0.8 }}> Password </Typography>
        <TextField name="password" placeholder="Enter password" fullWidth type={showPassword ? 'text' : 'password'}
          value={formValue.password} error={showError && !formValue.password}
          onChange={(e) => setFormValue(form => ({ ...form, password: e.target.value }))}
          helperText={showError && !formValue.password ? "You must enter a password" : ""}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box>
        <LoadingButton fullWidth variant="contained" loading={loading} onClick={onSubmit}
          sx={{ mt: 1, py: 1.3, textTransform: "none", borderRadius: 1, backgroundColor: "info.bright", "&:hover": { backgroundColor: "info.bright", } }}>
          Log In
        </LoadingButton>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: 14, color: "grey", textAlign: "center" }}> Don't have an account?  </Typography>
        <Typography sx={{ fontSize: 14, color: "grey", textAlign: "center", }}> Contact us at{" "}
          <Link href="mailto:Support@nhidirect.com" sx={{ color: "info.bright", textDecoration: "none", fontSize: "inherit", font: "inherit", }}>
            Support@nhidirect.com </Link>{" "}to request access
        </Typography>
      </Box>
    </Stack>
  </LoginFormWrapper>;
}
